import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Search, ArrowRight, ArrowLeft, Globe, MapPin, Zap, Award, Shield, AlertTriangle,
  Loader2, Lock, Target, Play, CheckCircle, Store, Star, Users,
  TrendingUp, ShoppingBag, MessageSquare, Camera, Smartphone, Heart,
  Share2, Copy, Check, ExternalLink
} from 'lucide-react';

const CATEGORY_META = {
  gbp_readiness: { label: 'GBP Readiness', icon: MapPin, color: '#0268C4' },
  website_relevance: { label: 'Website Relevance', icon: Globe, color: '#22C55E' },
  technical_seo: { label: 'Technical SEO', icon: Zap, color: '#DDA73D' },
  prominence: { label: 'Prominence', icon: Award, color: '#64748B' },
  risk_compliance: { label: 'Risk & Compliance', icon: Shield, color: '#EF4444' }
};
const IMPACT_COLORS = { High: '#EF4444', Med: '#DDA73D', Low: '#22C55E' };
const EFFORT_COLORS = { S: '#22C55E', M: '#DDA73D', L: '#EF4444' };

function getSessionId() {
  let sid = localStorage.getItem('localluc_guest_session');
  if (!sid) { sid = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2); localStorage.setItem('localluc_guest_session', sid); }
  return sid;
}

const BUSINESS_TYPES = [
  { value: 'Pizzeria', icon: Store, label: 'Pizzeria' },
  { value: 'Restaurant', icon: Store, label: 'Restaurant' },
  { value: 'Cafe', icon: Store, label: 'Cafe / Coffee' },
  { value: 'Bakery', icon: Store, label: 'Bakery' },
  { value: 'Bar & Grill', icon: Store, label: 'Bar & Grill' },
  { value: 'Food Truck', icon: Store, label: 'Food Truck' },
  { value: 'Other', icon: Store, label: 'Other' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

const ACTIVITIES = [
  { id: 'gbp', label: 'Google Business Profile', icon: MapPin },
  { id: 'social', label: 'Social media posts', icon: Camera },
  { id: 'reviews', label: 'Asking for reviews', icon: Star },
  { id: 'online_ordering', label: 'Online ordering', icon: ShoppingBag },
  { id: 'website_updates', label: 'Updating website regularly', icon: Globe },
  { id: 'email_marketing', label: 'Email marketing', icon: MessageSquare },
  { id: 'local_ads', label: 'Local ads (Google/Facebook)', icon: TrendingUp },
  { id: 'community', label: 'Community events/sponsorships', icon: Heart },
];

const GOALS = [
  { id: 'more_foot_traffic', label: 'Get more foot traffic', desc: 'I want more walk-ins and local customers' },
  { id: 'more_online_orders', label: 'Increase online orders', desc: 'I want more delivery and pickup orders' },
  { id: 'better_reviews', label: 'Get better reviews', desc: 'I want more and higher-rated reviews' },
  { id: 'beat_competition', label: 'Beat my competition', desc: 'I want to outrank nearby competitors' },
  { id: 'brand_awareness', label: 'Build brand awareness', desc: 'I want more people to know about us' },
  { id: 'new_location', label: 'Launch a new location', desc: 'I\'m opening somewhere new' },
];

const MATURITY = [
  { id: 'just_starting', label: 'Just starting out', desc: 'Brand new or barely online yet' },
  { id: 'invisible', label: 'Been around, but invisible online', desc: 'Good food, but nobody can find us' },
  { id: 'doing_okay', label: 'Doing okay, want to grow', desc: 'Some presence, ready to level up' },
  { id: 'strong', label: 'Strong presence, need to optimize', desc: 'Established, fine-tuning for the top' },
];

const CHALLENGES = [
  { id: 'no_time', label: 'No time for marketing' },
  { id: 'dont_know_where', label: 'Don\'t know where to start' },
  { id: 'bad_reviews', label: 'Dealing with bad reviews' },
  { id: 'competitors', label: 'Competitors ranking above me' },
  { id: 'website_old', label: 'Website is outdated' },
  { id: 'no_budget', label: 'Limited marketing budget' },
  { id: 'cant_find_us', label: 'People can\'t find us on Google' },
];

export default function HomePage() {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  // Form state
  const [url, setUrl] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('Restaurant');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [email, setEmail] = useState('');
  const [activities, setActivities] = useState([]);
  const [goals, setGoals] = useState([]);
  const [maturity, setMaturity] = useState('');
  const [challenges, setChallenges] = useState([]);

  const toggleActivity = (id) => setActivities(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  const toggleChallenge = (id) => setChallenges(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]);
  const toggleGoal = (id) => setGoals(prev => prev.includes(id) ? prev.filter(g => g !== id) : prev.length >= 3 ? prev : [...prev, id]);

  const STEPS = [
    { title: 'Your website', q: 'What\'s your website URL?', why: 'We\'ll look at your site the way Google does — checking what\'s working and what\'s holding you back from showing up in search results.' },
    { title: 'Your business', q: 'Tell us about your business', why: 'This helps us compare you to similar businesses in your area and give you recommendations that actually make sense for your type of food and location.' },
    { title: 'What you\'re doing', q: 'What are you already doing?', why: 'We won\'t waste your time suggesting stuff you\'re already on top of. This helps us skip the basics and focus on what will actually move the needle for you.' },
    { title: 'Your goals', q: 'Pick your top 3 goals', why: 'Your goals shape everything. We\'ll prioritize your action plan so the first things you see are the ones that push you closer to what matters most.' },
    { title: 'Your situation', q: 'Where are you right now?', why: 'A brand new spot needs different advice than a 10-year veteran. This keeps our recommendations realistic and doable for where you actually are.' },
    { title: 'Challenges', q: 'What\'s getting in your way?', why: 'If you\'re short on time, we\'ll focus on quick wins. Tight budget? We\'ll skip the expensive stuff. This makes your action plan fit your real life.' },
  ];

  const canProceed = () => {
    if (step === 0) return url.trim();
    if (step === 1) return true;
    return true; // All other steps are optional
  };

  const runAudit = async () => {
    setError('');
    setLoading(true);
    setResults(null);
    try {
      const goalLabels = goals.map(g => GOALS.find(gl => gl.id === g)?.label || g);
      const maturityLabel = MATURITY.find(m => m.id === maturity)?.label || '';

      const auditPayload = {
        website_url: url,
        business_name: businessName,
        business_type: businessType,
        city, state, email,
        intention: goalLabels[0] || 'Improve local search visibility',
        guest_session_id: getSessionId(),
        quiz_answers: {
          current_activities: activities,
          primary_goal: goalLabels[0] || '',
          goals: goalLabels,
          maturity: maturityLabel,
          challenges: challenges.map(c => CHALLENGES.find(ch => ch.id === c)?.label || c)
        }
      };

      // Step 1: Submit audit with retry (up to 3 attempts)
      let jobId = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const { data: jobData } = await api.post('/public/audit', auditPayload, {
            timeout: 15000,
            withCredentials: false
          });
          jobId = jobData.job_id;
          break;
        } catch (postErr) {
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 1500));
            continue;
          }
          throw postErr;
        }
      }
      if (!jobId) throw new Error('Failed to start audit');

      // Step 2: Poll for results every 3 seconds
      let consecutiveErrors = 0;
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const { data: status } = await api.get(`/public/audit/${jobId}`, {
            timeout: 10000,
            withCredentials: false
          });
          consecutiveErrors = 0;
          if (status.status === 'completed') {
            setResults(status);
            return;
          }
          if (status.status === 'failed') {
            setError(status.error || 'Audit failed. Please check the URL and try again.');
            return;
          }
        } catch (pollErr) {
          consecutiveErrors++;
          if (consecutiveErrors >= 5) {
            setError('Lost connection while waiting for results. Please try again.');
            return;
          }
        }
      }
      setError('The audit is taking longer than expected. Please try again in a few minutes.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) {
        setError(detail);
      } else if (!err.response) {
        setError('Could not reach the server after multiple attempts. Please check your connection and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const scoreColor = (s) => s > 80 ? '#22C55E' : s > 50 ? '#DDA73D' : '#EF4444';

  const handleShare = async () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }
    setShareLoading(true);
    try {
      const goalLabels = goals.map(g => GOALS.find(gl => gl.id === g)?.label || g);
      const { data } = await api.post('/shared-reports', {
        overall_score: results.overall_score,
        category_scores: results.category_scores,
        issues: results.issues,
        recommendations: results.recommendations,
        pages_crawled: results.pages_crawled,
        page_count: results.page_count,
        business_name: businessName,
        business_type: businessType,
        city, state,
        website_url: url,
        intention: goalLabels[0] || 'Improve local search visibility',
        primary_goal: goalLabels.join(', ')
      });
      const link = `${window.location.origin}/report/${data.token}`;
      setShareLink(link);
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Silently fail
    } finally {
      setShareLoading(false);
    }
  };

  // RESULTS VIEW
  if (results) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] pb-16">
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
            <button onClick={() => { setResults(null); setStep(0); }} className="flex items-center" data-testid="results-back-home">
              <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
            </button>
            <div className="flex items-center gap-2">
              <Button
                data-testid="results-share-button"
                variant="outline"
                onClick={handleShare}
                disabled={shareLoading}
                className="border-[#E2E8F0] text-[#64748B] hover:bg-[#EFF3F8] rounded-xl px-3 h-9 text-sm"
              >
                {shareLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : copied ? <><Check className="w-4 h-4 mr-1 text-[#22C55E]" /> Copied!</> : <><Share2 className="w-4 h-4 mr-1" /> Share</>}
              </Button>
              <Link to="/login" data-testid="results-login-link" className="text-sm text-[#64748B] hover:text-[#0F2951] hidden sm:inline">Sign in</Link>
              <Button data-testid="results-register-link" onClick={() => navigate('/register', { state: { guestSessionId: getSessionId() } })} className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-4 h-9 text-sm">Save Results</Button>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="audit-results-section">
          {/* Health Score */}
          <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 sm:p-8 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex flex-col items-center">
                <div className="relative w-28 h-28">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#EFF3F8" strokeWidth="8" />
                    <circle cx="50" cy="50" r="42" fill="none" stroke={scoreColor(results.overall_score)} strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={`${results.overall_score * 2.64} 264`} className="transition-all duration-1000" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-heading text-3xl font-semibold" style={{ color: scoreColor(results.overall_score) }}>{results.overall_score}</span>
                  </div>
                </div>
                <span className="text-xs text-[#64748B] mt-1">Health Score</span>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <h2 className="font-heading text-2xl font-medium text-[#0F2951] mb-1">
                  {businessName ? `${businessName}'s` : 'Your'} Online Visibility
                </h2>
                {(results.website_url || url) && (
                  <a href={results.website_url || url} target="_blank" rel="noopener noreferrer" data-testid="results-website-link"
                    className="inline-flex items-center gap-1 text-sm text-[#0268C4] hover:underline mb-1">
                    {(results.website_url || url).replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <p className="text-sm text-[#64748B]">{results.page_count} pages crawled &middot; {results.issues?.length || 0} issues found</p>
                {goals.length > 0 && <p className="text-sm text-[#0268C4] mt-1">Goal: {goals.map(g => GOALS.find(gl => gl.id === g)?.label).filter(Boolean).join(', ')}</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6">
              {Object.entries(results.category_scores || {}).map(([cat, score]) => {
                const meta = CATEGORY_META[cat];
                if (!meta) return null;
                const Icon = meta.icon;
                return (
                  <div key={cat} className="bg-[#F7F9FC] rounded-xl p-3 text-center">
                    <Icon className="w-4 h-4 mx-auto mb-1" style={{ color: meta.color }} strokeWidth={1.5} />
                    <div className="font-heading text-xl font-semibold" style={{ color: scoreColor(score) }}>{score}</div>
                    <div className="text-[10px] text-[#64748B] mt-0.5">{meta.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Recommendations */}
          {results.recommendations?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
              <h3 className="font-heading text-lg font-medium text-[#0F2951] mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#0268C4]" /> Your Top Moves
              </h3>
              <div className="space-y-3">
                {results.recommendations.slice(0, 5).map((r, i) => (
                  <div key={i} data-testid={`public-move-${i}`} className="flex items-start gap-3 p-3 bg-[#F7F9FC] rounded-xl">
                    <div className="w-6 h-6 bg-[#0268C4] rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0F2951]">{r.title}</p>
                      <p className="text-xs text-[#64748B] mt-0.5">{r.description}</p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] px-1.5" style={{ color: IMPACT_COLORS[r.impact] }}>{r.impact}</Badge>
                      <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] px-1.5" style={{ color: EFFORT_COLORS[r.effort] }}>{r.effort}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Issues */}
          {results.issues?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
              <h3 className="font-heading text-lg font-medium text-[#0F2951] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-[#EF4444]" /> Issues Found ({results.issues.length})
              </h3>
              <div className="space-y-2">
                {results.issues.slice(0, 8).map((issue, i) => (
                  <div key={i} data-testid={`public-issue-${i}`} className="flex items-center gap-3 p-3 rounded-xl bg-[#F7F9FC]">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: IMPACT_COLORS[issue.impact] }} />
                    <span className="text-sm text-[#0F2951] flex-1">{issue.title}</span>
                    <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] text-[#64748B]">{CATEGORY_META[issue.category]?.label || issue.category}</Badge>
                  </div>
                ))}
                {results.issues.length > 8 && <p className="text-sm text-[#64748B] text-center pt-2">+ {results.issues.length - 8} more issues</p>}
              </div>
            </div>
          )}

          {/* Share Score Card */}
          <div data-testid="share-score-card" className="bg-white rounded-2xl border border-[#E2E8F0] p-6 shadow-[0_4px_24px_rgba(44,48,46,0.04)]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#0268C4]/10 rounded-xl flex items-center justify-center">
                <Share2 className="w-5 h-5 text-[#0268C4]" />
              </div>
              <div>
                <h3 className="font-heading text-base font-medium text-[#0F2951]">Share this score with your team</h3>
                <p className="text-xs text-[#64748B]">Send a read-only link to partners, managers, or anyone who needs to see where you stand.</p>
              </div>
            </div>
            {shareLink ? (
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-[#F7F9FC] rounded-xl px-4 py-2.5 text-sm text-[#64748B] truncate border border-[#E2E8F0]">{shareLink}</div>
                <Button
                  data-testid="share-copy-link-btn"
                  onClick={() => { navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-4 h-10 flex-shrink-0"
                >
                  {copied ? <><Check className="w-4 h-4 mr-1" /> Copied!</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
                </Button>
              </div>
            ) : (
              <Button
                data-testid="share-generate-link-btn"
                onClick={handleShare}
                disabled={shareLoading}
                variant="outline"
                className="border-[#0268C4] text-[#0268C4] hover:bg-[#0268C4]/5 rounded-xl px-5 h-10"
              >
                {shareLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Share2 className="w-4 h-4 mr-2" />}
                Generate Share Link
              </Button>
            )}
          </div>

          {/* CTA: Register to save + download PDF */}
          <div className="bg-[#0F2951] rounded-2xl p-6 sm:p-8 text-center">
            <img src="/icon-nav.png" alt="Local Luc" className="w-14 h-14 rounded-xl object-cover mx-auto mb-3" />
            <h3 className="font-heading text-xl font-medium text-white mb-2">Download your full report & track progress</h3>
            <p className="text-sm text-white/70 mb-5 max-w-md mx-auto">Create a free account to download your PDF report, set AI-powered goals, track your score over time, and get cadence reminders.</p>
            <Button data-testid="public-register-cta" onClick={() => navigate('/register', { state: { guestSessionId: getSessionId() } })} className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-8 h-11 font-medium transition-all duration-200 hover:-translate-y-[1px]">
              Create Free Account <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          <div className="p-4 bg-[#EFF3F8] rounded-xl border border-[#E2E8F0]">
            <p className="text-xs text-[#64748B]">
              <strong>Disclaimer:</strong> Rankings depend on relevance, distance, and prominence. No tool can guarantee specific ranking positions.
              By using this tool you agree to our <Link to="/privacy" className="text-[#0268C4] underline">Privacy Policy</Link> and <Link to="/terms" className="text-[#0268C4] underline">Terms of Service</Link>.
              Your data is retained for 30 days.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // LOADING VIEW
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9FC] flex flex-col items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 border-4 border-[#E2E8F0] rounded-full" />
            <div className="absolute inset-0 border-4 border-t-[#0268C4] rounded-full animate-spin" />
          </div>
          <h2 className="font-heading text-xl font-medium text-[#0F2951] mb-2">Auditing your site...</h2>
          <p className="text-sm text-[#64748B]">Crawling pages, checking SEO signals, analyzing your online presence. This may take 15-30 seconds.</p>
        </div>
      </div>
    );
  }

  // QUIZ VIEW
  return (
    <div className="min-h-screen bg-[#F7F9FC] pb-16">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="home-logo">
            <img src="/icon-nav.png" alt="Local Luc" className="h-12 w-12 rounded-xl object-cover" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="text-sm text-[#64748B] hover:text-[#0F2951]">Sign in</Link>
            <Button data-testid="nav-register-link" onClick={() => navigate('/register')} className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-4 h-9 text-sm">Get Started</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12">
        {/* Wordmark centered above quiz */}
        <div className="flex justify-center mb-6">
          <img src="/logo-nav.png" alt="Local Luc" className="h-16 sm:h-24 rounded-2xl object-cover" data-testid="quiz-wordmark" />
        </div>
        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-8">
          {STEPS.map((_, i) => (
            <div key={i} className={`flex-1 h-1.5 rounded-full transition-all duration-300 ${i <= step ? 'bg-[#0268C4]' : 'bg-[#E2E8F0]'}`} />
          ))}
        </div>

        {error && <div className="mb-4 p-3 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl text-[#EF4444] text-sm">{error}</div>}

        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_4px_24px_rgba(44,48,46,0.04)] p-6 sm:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#0268C4] mb-2">Step {step + 1} of {STEPS.length}</p>
          <h2 className="font-heading text-xl sm:text-2xl font-medium text-[#0F2951] mb-2">{STEPS[step].q}</h2>
          <p className="text-sm text-[#64748B] mb-6">{STEPS[step].why}</p>

          {/* Step 0: Website URL */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Website URL</Label>
                <Input data-testid="quiz-url" value={url} onChange={e => setUrl(e.target.value)} placeholder="yourpizzashop.com" className="border-[#E2E8F0] bg-white rounded-xl h-12 text-base" autoFocus />
              </div>
              <p className="text-xs text-[#64748B]">Don't have a website? No worries — just type your business name and we'll still give you a Google profile audit.</p>
            </div>
          )}

          {/* Step 1: Business Info */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Business Name</Label>
                <Input data-testid="quiz-name" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Tony's Pizza" className="border-[#E2E8F0] bg-white rounded-xl h-11" autoFocus />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">What type of business?</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {BUSINESS_TYPES.map(bt => (
                    <button key={bt.value} data-testid={`quiz-type-${bt.value}`}
                      onClick={() => setBusinessType(bt.value)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-sm transition-all ${businessType === bt.value ? 'border-[#0268C4] bg-[#0268C4]/5 text-[#0268C4]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#0268C4]/30'}`}>
                      <bt.icon className="w-4 h-4" strokeWidth={1.5} /> {bt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">City</Label>
                  <Input data-testid="quiz-city" value={city} onChange={e => setCity(e.target.value)} placeholder="San Diego" className="border-[#E2E8F0] bg-white rounded-xl h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">State</Label>
                  <select data-testid="quiz-state" value={state} onChange={e => setState(e.target.value)}
                    className="flex h-11 w-full rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm text-[#0F2951] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0268C4] focus-visible:ring-offset-1">
                    <option value="">Select...</option>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Current Activities */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-[#22C55E]/10 rounded-xl mb-1">
                <CheckCircle className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#64748B]"><span className="font-medium text-[#0F2951]">Be honest — there's no wrong answer here.</span> If you're not doing any of these yet, that's totally fine. It just means there's more room to grow.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ACTIVITIES.map(act => (
                  <button key={act.id} data-testid={`quiz-activity-${act.id}`}
                    onClick={() => toggleActivity(act.id)}
                    className={`flex items-center gap-3 p-3.5 rounded-xl border text-sm text-left transition-all ${activities.includes(act.id) ? 'border-[#0268C4] bg-[#0268C4]/5 text-[#0268C4]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#0268C4]/30'}`}>
                    {activities.includes(act.id) ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <act.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />}
                    {act.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Goals — pick up to 3 */}
          {step === 3 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-[#64748B]">Pick the ones that matter most to you right now.</p>
                <Badge variant="outline" className="text-[10px] rounded-lg border-[#E2E8F0] text-[#0268C4]">{goals.length}/3 selected</Badge>
              </div>
              {GOALS.map(goal => (
                <button key={goal.id} data-testid={`quiz-goal-${goal.id}`}
                  onClick={() => toggleGoal(goal.id)}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${goals.includes(goal.id) ? 'border-[#0268C4] bg-[#0268C4]/5' : goals.length >= 3 ? 'border-[#E2E8F0] opacity-50 cursor-not-allowed' : 'border-[#E2E8F0] hover:border-[#0268C4]/30'}`}>
                  <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${goals.includes(goal.id) ? 'border-[#0268C4] bg-[#0268C4]' : 'border-[#E2E8F0]'}`}>
                    {goals.includes(goal.id) && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${goals.includes(goal.id) ? 'text-[#0268C4]' : 'text-[#0F2951]'}`}>{goal.label}</p>
                    <p className="text-xs text-[#64748B] mt-0.5">{goal.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 4: Maturity */}
          {step === 4 && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 bg-[#22C55E]/10 rounded-xl mb-1">
                <Target className="w-4 h-4 text-[#22C55E] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[#64748B]"><span className="font-medium text-[#0F2951]">No judgment here.</span> Whether you just opened last week or you've been around for 20 years, we'll meet you where you are.</p>
              </div>
              {MATURITY.map(m => (
                <button key={m.id} data-testid={`quiz-maturity-${m.id}`}
                  onClick={() => setMaturity(m.id)}
                  className={`w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${maturity === m.id ? 'border-[#0268C4] bg-[#0268C4]/5' : 'border-[#E2E8F0] hover:border-[#0268C4]/30'}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${maturity === m.id ? 'border-[#0268C4] bg-[#0268C4]' : 'border-[#E2E8F0]'}`}>
                    {maturity === m.id && <div className="w-2 h-2 bg-white rounded-full" />}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${maturity === m.id ? 'text-[#0268C4]' : 'text-[#0F2951]'}`}>{m.label}</p>
                    <p className="text-xs text-[#64748B] mt-0.5">{m.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Step 5: Challenges + Email */}
          {step === 5 && (
            <div className="space-y-5">
              <div className="space-y-3">
                <div className="flex items-start gap-2 p-3 bg-[#DDA73D]/10 rounded-xl">
                  <AlertTriangle className="w-4 h-4 text-[#DDA73D] flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-[#64748B]"><span className="font-medium text-[#0F2951]">Everyone has roadblocks — don't hold back.</span> The more honest you are, the more useful your action plan will be. We tailor everything to your real situation.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {CHALLENGES.map(ch => (
                    <button key={ch.id} data-testid={`quiz-challenge-${ch.id}`}
                      onClick={() => toggleChallenge(ch.id)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-sm text-left transition-all ${challenges.includes(ch.id) ? 'border-[#0268C4] bg-[#0268C4]/5 text-[#0268C4]' : 'border-[#E2E8F0] text-[#64748B] hover:border-[#0268C4]/30'}`}>
                      {challenges.includes(ch.id) ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <div className="w-4 h-4 rounded border border-[#E2E8F0] flex-shrink-0" />}
                      {ch.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2 pt-2">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#64748B]">Your Email (optional — to save results)</Label>
                <Input data-testid="quiz-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" className="border-[#E2E8F0] bg-white rounded-xl h-11" />
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
            <Button variant="ghost" onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} data-testid="quiz-back" className="text-[#64748B] hover:text-[#0F2951] rounded-xl">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={!canProceed()} data-testid="quiz-next" className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 transition-all duration-200 hover:-translate-y-[1px]">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={runAudit} data-testid="quiz-run-audit" className="bg-[#0268C4] hover:bg-[#015BA8] text-white rounded-xl px-6 h-11 transition-all duration-200 hover:-translate-y-[1px]">
                <Play className="w-4 h-4 mr-2" /> Run My Free Audit
              </Button>
            )}
          </div>
        </div>

        {/* Skip option */}
        {step < STEPS.length - 1 && step > 0 && (
          <button onClick={() => setStep(s => s + 1)} data-testid="quiz-skip" className="block mx-auto mt-3 text-xs text-[#64748B] hover:text-[#0268C4] transition-colors">
            Skip this step
          </button>
        )}

        {/* Footer links */}
        <div className="text-center mt-8">
          <p className="text-xs text-[#64748B]">
            <Link to="/privacy" className="hover:text-[#0268C4] transition-colors">Privacy Policy</Link>
            {' '}&middot;{' '}
            <Link to="/terms" className="hover:text-[#0268C4] transition-colors">Terms of Service</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
